/**
 * usePinsAndBookmarks Hook
 *
 * Manages pinned messages (per session) and bookmarked messages (cross-session).
 * Persisted in localStorage so they survive page refreshes.
 */

import { useState, useCallback, useEffect } from 'react';

const PINS_KEY = 'imago_chat_pins';
const BOOKMARKS_KEY = 'imago_chat_bookmarks';

/** Read JSON from localStorage safely */
const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

/** Write JSON to localStorage */
const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota exceeded — silently fail */ }
};

/**
 * @returns {Object} pins and bookmarks state and actions
 */
export function usePinsAndBookmarks() {
  // Pins: { [sessionId]: [{ message_id, content, timestamp }] }
  const [allPins, setAllPins] = useState(() => readStorage(PINS_KEY, {}));

  // Bookmarks: [{ message_id, session_id, session_title, content, timestamp, bookmarked_at }]
  const [bookmarks, setBookmarks] = useState(() => readStorage(BOOKMARKS_KEY, []));

  // Persist to localStorage on change
  useEffect(() => { writeStorage(PINS_KEY, allPins); }, [allPins]);
  useEffect(() => { writeStorage(BOOKMARKS_KEY, bookmarks); }, [bookmarks]);

  /** Get pins for a specific session */
  const getPins = useCallback((sessionId) => {
    return allPins[sessionId] || [];
  }, [allPins]);

  /** Pin a message in a session */
  const pinMessage = useCallback((sessionId, msg) => {
    setAllPins((prev) => {
      const sessionPins = prev[sessionId] || [];
      if (sessionPins.some((p) => p.message_id === msg.message_id)) return prev;
      // Limit to 20 pins per session
      const updated = [...sessionPins, {
        message_id: msg.message_id,
        content: msg.content,
        timestamp: msg.timestamp,
        role: msg.role,
      }].slice(-20);
      return { ...prev, [sessionId]: updated };
    });
  }, []);

  /** Unpin a message from a session */
  const unpinMessage = useCallback((sessionId, messageId) => {
    setAllPins((prev) => {
      const sessionPins = prev[sessionId] || [];
      return {
        ...prev,
        [sessionId]: sessionPins.filter((p) => p.message_id !== messageId),
      };
    });
  }, []);

  /** Check if a message is pinned */
  const isPinned = useCallback((sessionId, messageId) => {
    return (allPins[sessionId] || []).some((p) => p.message_id === messageId);
  }, [allPins]);

  /** Bookmark a message (cross-session) */
  const bookmarkMessage = useCallback((sessionId, sessionTitle, msg) => {
    setBookmarks((prev) => {
      if (prev.some((b) => b.message_id === msg.message_id)) return prev;
      // Limit to 100 bookmarks total
      return [...prev, {
        message_id: msg.message_id,
        session_id: sessionId,
        session_title: sessionTitle || 'Chat',
        content: msg.content,
        timestamp: msg.timestamp,
        role: msg.role,
        bookmarked_at: new Date().toISOString(),
      }].slice(-100);
    });
  }, []);

  /** Remove a bookmark */
  const unbookmarkMessage = useCallback((messageId) => {
    setBookmarks((prev) => prev.filter((b) => b.message_id !== messageId));
  }, []);

  /** Check if a message is bookmarked */
  const isBookmarked = useCallback((messageId) => {
    return bookmarks.some((b) => b.message_id === messageId);
  }, [bookmarks]);

  return {
    getPins,
    pinMessage,
    unpinMessage,
    isPinned,
    bookmarks,
    bookmarkMessage,
    unbookmarkMessage,
    isBookmarked,
  };
}

export default usePinsAndBookmarks;
